import { APP_INITIALIZER, ErrorHandler, isDevMode, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { HttpInterceptorFn, provideHttpClient, withInterceptors } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { provideAppUpdateHandler } from '@propmix/app-update-handler';
import {
  AppraisalAssistantChatWidgetComponent,
  AppraisalChatWidgetConfig,
} from '@propmix/appraisal-assistant-chat-widget';
import { CommonHeaderModule, CommonHeaderService, HeaderConfig, PROFET_APPS } from '@propmix/profet-common-header';
import { CookieService } from 'ngx-cookie-service';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { Observable } from 'rxjs';
import { environment as ENV } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AngularMaterialModule } from './module/angular-material/angular-material.module';
import { GOOGLE_MAP_ID } from './module/google-map/interface/google-map.interface';
import { ShowIfPermittedDirective } from './module/order-list/directives/show-if-permitted.directive';
import { BathroomDecimalPipe } from './module/property/pipe/bathroom-decimal.pipe';
import { SyncModule } from './module/sync/sync.module';
import { AccessControlDirective } from './shared/directives/access-control.directive';
import { SlidingSheetDirective } from './shared/directives/sliding-sheet.directive';
import { GlobalErrorHandler } from './shared/error-handler/global-error-handler';
import { InitializerService } from './shared/initializer/initializer.service';
import { authTokenInterceptor } from './shared/interceptor/auth-token.interceptor';
import { lockTokenInterceptor } from './shared/interceptor/lock-token.interceptor';
import { readOnlyInterceptor } from './shared/interceptor/read-only.interceptor';
import { DynamicPipe } from './shared/pipes/dynamic.pipe';
import { EmptyDataPipe } from './shared/pipes/empty-data.pipe';
import { EnumRefPipe } from './shared/pipes/enum-ref.pipe';
import { ImagePipe } from './shared/pipes/image.pipe';
import { OrderMultiDataSourcePipe } from './shared/pipes/order-multi-data-source.pipe';
import { SelectedSourceDataPipe } from './shared/pipes/selected-source-data.pipe';
import { SimpleDatePipe } from './shared/pipes/simple-date.pipe';
import { UnitAffixPipe } from './shared/pipes/unitAffix.pipe';
import { SharedModule } from './shared/shared.module';
import { CompanyState } from './shared/state/company.state';

export function appInitializer(initializerService: InitializerService): () => Observable<void> {
  return () => initializerService.initializeApp();
}

export function httpInterceptorFactory(): HttpInterceptorFn[] {
  const interceptorList: HttpInterceptorFn[] = [authTokenInterceptor, readOnlyInterceptor, lockTokenInterceptor];

  // if (ENV.useMockApi) {
  //   interceptorList.push(mockApiInterceptor);
  // }

  return interceptorList;
}

export function initializeHeaderConfig(): HeaderConfig {
  const headerConfig: HeaderConfig = {
    apiUrl: `${ENV.portal.backendBaseUrl}`,
    appType: PROFET_APPS.appraisal_valuation,
    portalFrontendUrl: ENV.portal.frontendBaseUrl,
    signOutUrl: `${ENV.logInUrl}`,
    enableLastUrlRedirection: true,
  };

  return headerConfig;
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SharedModule,
    AngularMaterialModule,
    SlidingSheetDirective,
    SyncModule,
    CommonHeaderModule,
    EmptyDataPipe,
    AppraisalAssistantChatWidgetComponent,
    AccessControlDirective,
    ShowIfPermittedDirective,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    provideHttpClient(withInterceptors(httpInterceptorFactory())),
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializer,
      deps: [InitializerService],
      multi: true,
    },
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: true },
    },
    SelectedSourceDataPipe,
    CurrencyPipe,
    DecimalPipe,
    SimpleDatePipe,
    DatePipe,
    EnumRefPipe,
    DynamicPipe,
    EmptyDataPipe,
    BathroomDecimalPipe,
    OrderMultiDataSourcePipe,
    ImagePipe,
    provideEnvironmentNgxMask(),
    UnitAffixPipe,
    CommonHeaderService,
    { provide: 'headerConfig', useFactory: initializeHeaderConfig },
    {
      provide: 'appraisalAssistConfig',
      useValue: {
        apiUrl: `${ENV.portal.backendBaseUrl}/api/user/chat`,
      } as AppraisalChatWidgetConfig,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    CookieService,
    provideAppUpdateHandler(),
    {
      provide: GOOGLE_MAP_ID,
      useFactory: (company: CompanyState) => company.companySetting.googleMapId,
      deps: [CompanyState],
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
